import Constants from 'expo-constants';
import { Platform } from 'react-native';

function getHostFromAddress(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const normalized = value.includes('://') ? value : `http://${value}`;

  try {
    return new URL(normalized).hostname;
  } catch {
    const [host] = value.split(':');
    return host || null;
  }
}

function getExpoDevHost() {
  const candidates = [
    Constants.expoConfig?.hostUri,
    (Constants.manifest2 as { extra?: { expoGo?: { debuggerHost?: string } } } | null)?.extra
      ?.expoGo?.debuggerHost,
    (Constants.expoGoConfig as { debuggerHost?: string } | null)?.debuggerHost,
  ];

  for (const candidate of candidates) {
    const host = getHostFromAddress(candidate);
    if (host) {
      return host;
    }
  }

  return null;
}

function getDefaultAPIBaseURL() {
  if (Platform.OS === 'web') {
    // Production web should never inherit a developer-local API host from dotenv.
    if (!__DEV__) {
      return 'https://api.radious.app/v1';
    }
    // In dev, the browser runs on the same machine as the API.
    return 'http://localhost:9090/v1';
  }

  // Native devices need to reach the dev machine over the network
  const expoDevHost = getExpoDevHost();
  if (expoDevHost) {
    return `http://${expoDevHost}:9090/v1`;
  }

  // Fallbacks for emulators
  if (Platform.OS === 'android') {
    return 'http://10.0.2.2:9090/v1';
  }
  return 'http://127.0.0.1:9090/v1';
}

// Debug: log the detected platform and URL
if (__DEV__) {
  console.log('[config] Platform.OS:', Platform.OS);
  console.log('[config] EXPO_PUBLIC_API_URL:', process.env.EXPO_PUBLIC_API_URL);
  console.log('[config] EXPO_PUBLIC_WEB_API_URL:', process.env.EXPO_PUBLIC_WEB_API_URL);
}

function envNumber(value: string | undefined, fallback: number) {
  if (!value) {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function isLocalAPIBaseURL(value: string) {
  try {
    const host = new URL(value).hostname;
    return host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.');
  } catch {
    return value.includes('localhost') || value.includes('127.0.0.1') || value.includes('192.168.');
  }
}

function getConfiguredAPIBaseURL() {
  const envURL = process.env.EXPO_PUBLIC_API_URL;

  if (Platform.OS === 'web' && !__DEV__) {
    return envURL && !isLocalAPIBaseURL(envURL) ? envURL : 'https://api.radious.app/v1';
  }

  // Optional local-dev escape hatch for WSL/browser networking.
  if (
    Platform.OS === 'web' &&
    process.env.EXPO_PUBLIC_WEB_API_URL &&
    (__DEV__ || !isLocalAPIBaseURL(process.env.EXPO_PUBLIC_WEB_API_URL))
  ) {
    return process.env.EXPO_PUBLIC_WEB_API_URL;
  }

  // Production env var stays the only required API URL.
  if (envURL) {
    return envURL;
  }

  // Dev fallbacks
  return getDefaultAPIBaseURL();
}

const _apiBaseURL = getConfiguredAPIBaseURL();

// Debug: log the final API URL
if (__DEV__) {
  console.log('[config] Final apiBaseURL:', _apiBaseURL);
}

export const APP_CONFIG = {
  apiBaseURL: _apiBaseURL,
  posthogKey: process.env.EXPO_PUBLIC_POSTHOG_KEY ?? '',
  posthogHost: process.env.EXPO_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com',
  webBaseURL: process.env.EXPO_PUBLIC_WEB_BASE_URL ?? 'https://radious.app',
  defaultLocation: {
    latitude: envNumber(process.env.EXPO_PUBLIC_DEFAULT_LAT, 29.4241),
    longitude: envNumber(process.env.EXPO_PUBLIC_DEFAULT_LNG, -98.4936),
  },
  eventPageSize: 40,
  eventTags: (
    process.env.EXPO_PUBLIC_EVENT_TAGS ??
    'byob,live_music,dj,karaoke,comedy,trivia,food,open_bar,food_trucks,outdoors,indoors,rooftop,pool_party,meetup,lgbtq,art,sports,board_games,casual,business_casual,formal,costume,smoking_allowed,pet_friendly,kid_friendly,free_entry'
  ).split(','),
  supportEmail: process.env.EXPO_PUBLIC_SUPPORT_EMAIL ?? 'hello@radious.app',
  supportURL: process.env.EXPO_PUBLIC_SUPPORT_URL ?? 'https://radious.app/support',
  downloadURL: process.env.EXPO_PUBLIC_DOWNLOAD_URL ?? 'https://radious.app/download',
  statusURL: process.env.EXPO_PUBLIC_STATUS_URL ?? 'https://status.radious.app/public.json',
  appStoreURL:
    process.env.EXPO_PUBLIC_APP_STORE_URL ??
    'https://apps.apple.com/us/app/radious-local-events/id6760730711',
  playStoreURL: process.env.EXPO_PUBLIC_PLAY_STORE_URL ?? '',
  backstageURL: process.env.EXPO_PUBLIC_BACKSTAGE_URL ?? 'https://backstage.radious.app/backstage',
  marketingURL: process.env.EXPO_PUBLIC_MARKETING_URL ?? 'https://radious.app/marketing',
  privacyPolicyURL: process.env.EXPO_PUBLIC_PRIVACY_URL ?? 'https://docs.radious.app/privacy',
  termsURL: process.env.EXPO_PUBLIC_TERMS_URL ?? 'https://docs.radious.app/terms',
} as const;
